import { accountsActions, accountsSelectors } from 'app-components/flux/accounts';
import {
  preferencesActions as actions,
  preferencesSagas,
  preferencesSelectors,
  preferencesUtils,
} from 'app-components/flux/preferences';
import { getEnvironmentConfig, getLogger } from 'app-components/utils/core';
import { defaultDatasetPreferences } from 'data/preferences/defaults';
import { fieldData, requiredColumns } from 'data/transactions/config.ts';
/*
 * PREFERENCE STORE SAGAS
 *
 * These routines listen for request actions, and satisfies them.  These are asynchronous routines, and upon
 * completion will dispatch a new action with the results
 *
 *
 */
import { List as ImmutableList } from 'immutable';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import AxiosFactory from 'utils/axiosFactory';

const log = getLogger('data/preferences/sagas.js');

function constructPreferencesUrl(data) {
  let requestUrl;
  let urlSection;

  switch (data?.section) {
    case 'shared':
      urlSection = data.group === 'dataset' ? '' : `/${data.group}`;
      requestUrl = `${getEnvironmentConfig().services_url}/preferences${urlSection}`;
      break;

    case 'client':
      urlSection = data.group === 'dataset' ? '' : `/${data.group}`;
      requestUrl = `${getEnvironmentConfig().services_url}/preferences/client${urlSection}`;
      break;

    default:
      requestUrl = `${getEnvironmentConfig().services_url}/preferences`;
      break;
  }

  return requestUrl;
}

export function* resetAllPreferences() {
  try {
    const qcsAxios = AxiosFactory.get('qcs');
    const data = {
      section: 'shared',
      group: 'dataset',
      preference: null,
    };
    // first set redux prefs to defaults with a faux response
    yield put(
      actions.setPreferenceSuccess({
        data,
        response: { data: defaultDatasetPreferences },
      }),
    );

    // now null out the prefs on QCS
    const requestUrl = constructPreferencesUrl(data);

    yield call(
      qcsAxios.put,
      requestUrl,
      { webApp: null },
      data.group === 'user' && { headers: { 'qcs-dataset-id': null } },
    );
  } catch (error) {
    assert(false, error);

    log.log('Error in resetting preferences');
  }
}

// ====================================================
// DELETE PREFERENCE
// if you want to use yield, put a star here
export function* deletePreference({ payload: _data }) {
  /*
    let requestUrl = `${getEnvironmentConfig().services_url}/preferences`;
    requestUrl += `/${data.id}`;
    log.log(`In SAGAS: Delete Preference ${requestUrl}`, data);

    try {
      const qcsAxios = AxiosFactory.get('qcs');

      // yield below causes the call to happen async, code following it is the
      // callback functions (one for success, one for error)
      //
      // assumes that the data passed in is valid and per the QCS spec
      yield call(qcsAxios.delete, requestUrl, data);

      // create an immutable object
      // const preference = types.Preference(data);
      yield put(actions.deletePreferenceSuccess(data.id));
    } catch (error) {
      log.log('Update Preference Error...', error);
      yield put(actions.deletePreferenceFailure(error));
    }
  */
}

// ====================================================
// INIT ACCOUNT PREFERENCES
export function* initAccountPreferences() {
  const accountsPending = yield select(accountsSelectors.getLoadPending);
  const prefsPending = yield select(preferencesSelectors.getLoadPendingState);
  if (!accountsPending && !prefsPending.shared.dataset) {
    const accounts = yield select(accountsSelectors.getAccountsById);
    const acctPrefs = yield select(preferencesSelectors.allAccountPreferences);

    if (accounts) {
      const newAcctPrefs = preferencesUtils.initAccountPrefs(accounts, acctPrefs);

      if (newAcctPrefs) {
        const prefObj = {
          section: 'shared',
          group: 'dataset',
          preference: { webApp: { accountsPrefs: newAcctPrefs } },
        };
        yield put(actions.setPreference(prefObj, { context: 'initAccountPreferences1' }));
      }
    }
    //
    // Also check if the transaction columns config was updated with new defaults
    // if the required defaults in the code contain any columns NOT in the registerPrefs, then
    // add required defaults in
    //
    const prefs = yield select(preferencesSelectors.getSharedPreferencesByPath, { group: 'dataset', path: ['webApp'] });

    const buildRegFields = (regFields, regFieldsDefault) => {
      let newRegFields = regFields || ImmutableList();
      regFieldsDefault?.forEach((col) => {
        if (!newRegFields.includes(col)) {
          const columnPosition = fieldData[col]?.position;
          const slot = newRegFields.findIndex((x) => fieldData[x]?.position > columnPosition);
          newRegFields = newRegFields.insert(slot, col);
        }
      });
      return newRegFields;
    };
    const regFieldsCashFlow = prefs.transactionRegister.regFieldsSort.cashFlow;
    const newFieldsCashFlow = buildRegFields(regFieldsCashFlow, requiredColumns.default);
    const regFieldsInvestment = prefs.transactionRegister.regFieldsSort.investment;
    const newRegFieldsInvestment = buildRegFields(regFieldsInvestment, requiredColumns.investment);
    if (newFieldsCashFlow !== regFieldsCashFlow || newRegFieldsInvestment !== regFieldsInvestment) {
      const prefObj = {
        section: 'shared',
        group: 'dataset',
        preference: {
          webApp: {
            transactionRegister: {
              regFieldsSort: {
                cashFlow: newFieldsCashFlow,
                investment: newRegFieldsInvestment,
              },
            },
          },
        },
      };
      yield put(actions.setPreference(prefObj, { context: 'initAccountPreferences2' }));
    }
  }
}

export function* getAccountsSuccessActionWatcher() {
  yield takeEvery(accountsActions.getAccountsSuccess, initAccountPreferences);
}

export function* upsertAccountsSuccessActionWatcher() {
  yield takeEvery(accountsActions.batchAccountsSuccess, initAccountPreferences);
}

export function* initAccountPreferencesActionWatcher() {
  yield takeEvery(actions.initAccountPreferences, initAccountPreferences);
}

export function* resetAllPreferencesActionWatcher() {
  yield takeEvery(actions.resetAllPreferences, resetAllPreferences);
}

export function* getPreferencesSuccessActionWatcher() {
  yield takeEvery(actions.getPreferencesSuccess, initAccountPreferences);
}

export function* deletePreferenceActionWatcher() {
  yield takeEvery(actions.deletePreference, deletePreference);
}

// ====================================================
// EXPORTS

export default [
  ...preferencesSagas,
  deletePreferenceActionWatcher,
  getAccountsSuccessActionWatcher,
  getPreferencesSuccessActionWatcher,
  initAccountPreferencesActionWatcher,
  resetAllPreferencesActionWatcher,
  upsertAccountsSuccessActionWatcher,
];
